import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AppIcon, MenuIcon } from '../../components/icons';
import { prefetchDashboardPath } from '../../app/routes/dashboardChunkPrefetch';
import { hapticTap } from '../../shared/native/nativeApp';
import type { NavEntry, NavLinkEntry } from './navConfig';
import './MobileTabBar.css';

export type MobileTabBarProps = {
  navEntries: NavEntry[];
  isActive: (path: string) => boolean;
  onOpenMenu: () => void;
};

/**
 * Destinations worth a permanent slot on the tab bar. Anything else stays in
 * the drawer behind the Menu tab. Kept in nav order, capped at 4 + Menu.
 */
const TAB_PRIORITY = new Set([
  'Dashboard',
  'AI Grader',
  'Job Openings',
  'My Sessions',
  'My Services',
  'Payouts',
]);

const MAX_TABS = 4;

/** Short labels that fit an iOS tab bar. */
const TAB_LABELS: Record<string, string> = {
  'AI Grader': 'AI Grader',
  'Job Openings': 'Jobs',
  'My Sessions': 'Sessions',
  'My Services': 'Services',
};

export const MobileTabBar = memo(function MobileTabBar({
  navEntries,
  isActive,
  onOpenMenu,
}: MobileTabBarProps) {
  const tabs = useMemo<NavLinkEntry[]>(
    () =>
      navEntries
        .filter((e): e is NavLinkEntry => e.type === 'link' && TAB_PRIORITY.has(e.label))
        .slice(0, MAX_TABS),
    [navEntries]
  );

  return (
    <nav className="mobile-tab-bar" aria-label="Primary">
      {tabs.map((tab) => {
        const active = isActive(tab.path);
        return (
          <Link
            key={tab.path}
            to={tab.path}
            className={`mobile-tab ${active ? 'mobile-tab--active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => void hapticTap()}
            onTouchStart={() => prefetchDashboardPath(tab.path)}
          >
            <AppIcon icon={tab.icon} className="mobile-tab__icon" size={22} />
            <span className="mobile-tab__label">{TAB_LABELS[tab.label] ?? tab.label}</span>
          </Link>
        );
      })}
      <button
        type="button"
        className="mobile-tab"
        onClick={() => {
          void hapticTap();
          onOpenMenu();
        }}
        aria-label="Open menu"
      >
        <AppIcon icon={MenuIcon} className="mobile-tab__icon" size={22} />
        <span className="mobile-tab__label">Menu</span>
      </button>
    </nav>
  );
});
